import React from 'react';
import { Plus, X } from 'lucide-react';
import { Button } from '@dashboard/components/ui/button';
import { Input } from '@dashboard/components/ui/input';
import TicketTypeRestriction, { type TicketTypeOption } from './TicketTypeRestriction';

export interface RecipientRow {
  /** Stable id so React keeps focus on the right input across add/remove. */
  id: number;
  email: string;
  name: string;
  /** Whitelist of TicketType ids; empty = all. Mirrors API field. */
  allowedTicketTypes: string[];
}

export const newRow = (id: number): RecipientRow => ({
  id,
  email: '',
  name: '',
  allowedTicketTypes: [],
});

const isRowEmpty = (row: RecipientRow) =>
  !row.email.trim() && !row.name.trim() && row.allowedTicketTypes.length === 0;

interface Props {
  rows: RecipientRow[];
  onChange: (rows: RecipientRow[]) => void;
  nextId: number;
  onAdvanceNextId: () => void;
  ticketTypeOptions: TicketTypeOption[];
  disabled?: boolean;
}

// Step 1 row table for the invite wizard. Lifted from the old InviteForm
// so the wizard owns the canonical rows[] state across the three steps.
// Per-row Ticket-scanning picker (per the updated mockup); each row
// carries its own `allowedTicketTypes` array.
const RecipientRows: React.FC<Props> = ({
  rows,
  onChange,
  nextId,
  onAdvanceNextId,
  ticketTypeOptions,
  disabled,
}) => {
  const setField = (id: number, patch: Partial<RecipientRow>) => {
    onChange(rows.map(r => (r.id === id ? { ...r, ...patch } : r)));
  };
  // Clear the last remaining row in place; otherwise drop it from the
  // list. Always keeps the table non-empty so the merchant has somewhere
  // to start typing.
  const removeRow = (id: number) => {
    if (rows.length === 1) {
      onChange([{ id: rows[0].id, email: '', name: '', allowedTicketTypes: [] }]);
      return;
    }
    onChange(rows.filter(r => r.id !== id));
  };
  const addRow = () => {
    onChange([...rows, newRow(nextId)]);
    onAdvanceNextId();
  };

  return (
    <div>
      <div className="hidden sm:grid grid-cols-[1fr_1fr_minmax(140px,auto)_2.5rem] gap-2 items-center mb-1 text-[0.7rem] font-medium uppercase tracking-wide text-muted-foreground">
        <div>Email</div>
        <div>Name (optional)</div>
        <div>Ticket scanning</div>
        <div />
      </div>

      <div className="space-y-2">
        {rows.map((row, idx) => (
          <div
            key={row.id}
            className="grid grid-cols-[1fr_2.5rem] sm:grid-cols-[1fr_1fr_minmax(140px,auto)_2.5rem] gap-2 items-center max-sm:border-b max-sm:pb-3 max-sm:last:border-b-0 max-sm:[&>*:not(:last-child)]:col-start-1 max-sm:[&>*:last-child]:col-start-2 max-sm:[&>*:last-child]:row-start-1"
          >
            <Input
              type="email"
              placeholder="e.g. jane@example.com"
              value={row.email}
              onChange={e => setField(row.id, { email: e.target.value })}
              disabled={disabled}
              aria-label={`Email for row ${idx + 1}`}
            />
            <Input
              type="text"
              placeholder="e.g. Jane Doe"
              value={row.name}
              onChange={e => setField(row.id, { name: e.target.value })}
              disabled={disabled}
              aria-label={`Name for row ${idx + 1}`}
            />
            <TicketTypeRestriction
              options={ticketTypeOptions}
              value={row.allowedTicketTypes}
              onChange={next => setField(row.id, { allowedTicketTypes: next })}
              disabled={disabled}
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="size-9 p-0 flex-shrink-0"
              // Single-row X clears in place; only fully-disabled when the
              // row is already blank so the click would be a no-op.
              disabled={disabled || (rows.length === 1 && isRowEmpty(row))}
              onClick={() => removeRow(row.id)}
              aria-label={`Remove row ${idx + 1}`}
            >
              <X className="size-4" />
            </Button>
          </div>
        ))}
      </div>

      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={addRow}
        disabled={disabled}
        className="mt-2 h-8 text-muted-foreground"
      >
        <Plus className="size-3.5 mr-1" />
        Add another
      </Button>
    </div>
  );
};

export default RecipientRows;
